/**
 * Draggable participant row for signing ceremony creation.
 *
 * Uses react-dnd (already in dependencies, pattern from ProcessVisualEditor.tsx)
 * for drag-and-drop reordering of signing participants.
 *
 * Supports two states:
 * - Assigned: full opacity, name/email shown
 * - Unassigned: dimmed opacity, Select dropdown for actor assignment
 */

import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Paper,
  Group,
  TextInput,
  Select,
  Button,
  Badge,
  ActionIcon,
  Text,
} from '@mantine/core';
import { IconGripVertical, IconTrash } from '@tabler/icons-react';
import type { ParticipantDefault, ActorOption } from '@/hooks/useCeremonyDefaults';
import { getCeremonyRoleLabel } from '@/hooks/useCeremonyDefaults';

const ITEM_TYPE = 'CEREMONY_PARTICIPANT';

interface DragItem {
  index: number;
}

interface DraggableParticipantRowProps {
  participant: ParticipantDefault;
  index: number;
  moveParticipant: (fromIndex: number, toIndex: number) => void;
  onUpdate: (index: number, field: keyof ParticipantDefault, value: string) => void;
  onAssignActor: (index: number, actor: ActorOption) => void;
  onRemove: (index: number) => void;
  availableActors: ActorOption[];
  canRemove: boolean;
  showDragHandle: boolean;
  roleOptions: Array<{ value: string; label: string }>;
}

export function DraggableParticipantRow({
  participant,
  index,
  moveParticipant,
  onUpdate,
  onAssignActor,
  onRemove,
  availableActors,
  canRemove,
  showDragHandle,
  roleOptions,
}: Readonly<DraggableParticipantRowProps>) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (draggedItem: DragItem) => {
      if (draggedItem.index !== index) {
        moveParticipant(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  // Combine refs for drag preview and drop target
  dragPreview(drop(ref));

  const isAssigned = participant.assigned;
  const roleLabel = getCeremonyRoleLabel(participant.ceremonyRole);

  return (
    <Paper
      ref={ref}
      p="sm"
      withBorder
      style={{
        opacity: isDragging ? 0.4 : isAssigned ? 1 : 0.5,
        cursor: showDragHandle ? 'default' : 'default',
      }}
    >
      <Group gap="sm" align="flex-end" wrap="nowrap">
        {/* Drag handle — only shown for sequential ordering */}
        {showDragHandle && (
          <ActionIcon
            ref={(node: HTMLButtonElement | null) => { drag(node); }}
            variant="subtle"
            color="gray"
            size="lg"
            style={{ cursor: 'grab' }}
            aria-label="Drag to reorder"
          >
            <IconGripVertical size={18} />
          </ActionIcon>
        )}

        {/* Order number */}
        <Badge variant="light" color={isAssigned ? 'blue' : 'gray'} size="lg" w={32}>
          {index + 1}
        </Badge>

        {/* Role label */}
        <Badge
          variant="light"
          color={isAssigned ? 'teal' : 'gray'}
          size="sm"
          style={{ minWidth: 120 }}
        >
          {roleLabel}
        </Badge>

        {/* Name — TextInput if assigned, Select if not */}
        {isAssigned ? (
          <TextInput
            label="Name"
            value={participant.name}
            onChange={(e) => onUpdate(index, 'name', e.currentTarget.value)}
            style={{ flex: 1 }}
            size="sm"
          />
        ) : (
          <Select
            label="Assign Actor"
            placeholder={`Select ${roleLabel}...`}
            data={availableActors}
            onChange={(actorId) => {
              const actor = availableActors.find((a) => a.value === actorId);
              if (actor) onAssignActor(index, actor);
            }}
            style={{ flex: 1 }}
            size="sm"
            searchable
          />
        )}

        {/* Email */}
        <TextInput
          label="Email"
          value={participant.email}
          onChange={(e) => onUpdate(index, 'email', e.currentTarget.value)}
          style={{ flex: 1 }}
          size="sm"
          disabled={!isAssigned}
          placeholder={isAssigned ? '' : 'Auto-filled on assign'}
        />

        {/* Role selector */}
        <Select
          label="Role"
          data={roleOptions}
          value={participant.role}
          onChange={(v) => v && onUpdate(index, 'role', v)}
          size="sm"
          style={{ width: 130 }}
        />

        {/* Remove button */}
        {canRemove && (
          <Button
            variant="light"
            color="red"
            size="xs"
            leftSection={<IconTrash size={14} />}
            onClick={() => onRemove(index)}
          >
            <Text size="xs" visibleFrom="sm">Remove</Text>
          </Button>
        )}
      </Group>
    </Paper>
  );
}
